/**
 * Liste des utilisateurs avec leurs métriques.
 * Rendu optimisé avec pagination pour performance.
 * Conforme WCAG 2.1 AA.
 */
import React, { useMemo, useState } from 'react';
import { User, ChevronLeft, ChevronRight } from 'lucide-react';

interface UserMetrics {
  user_id: number;
  email: string;
  agents_count: number;
  subjects_count: number;
  media_count: number;
  conversations_count: number;
  storage_bytes: number;
  created_at: string;
}

interface UsersSummaryProps {
  data: UserMetrics[];
}

export default function UsersSummary({ data }: UsersSummaryProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => b.agents_count - a.agents_count);
  }, [data]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  if (data.length === 0) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-8 text-center text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
        Aucun utilisateur trouvé
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-2 mb-4" role="list" aria-label="Liste des utilisateurs">
        {currentData.map((user) => (
          <div 
            key={user.user_id} 
            className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-900" 
            role="listitem"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <User className="w-8 h-8 text-zinc-400 dark:text-zinc-500" aria-hidden="true" />
                <div>
                  <p className="font-semibold text-zinc-900 dark:text-white">{user.email}</p>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">
                    {user.agents_count} agents · {user.subjects_count} connaissances · {formatBytes(user.storage_bytes)}
                  </p>
                </div>
              </div>
              <div className="px-3 py-1 rounded-full bg-zinc-100 dark:bg-zinc-800 text-sm text-zinc-700 dark:text-zinc-300">
                {user.conversations_count} conversations
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-zinc-200 dark:border-zinc-700 pt-4">
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Affichage {startIndex + 1}-{Math.min(endIndex, sortedData.length)} sur {sortedData.length} utilisateurs
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 rounded-lg bg-zinc-100 text-zinc-700 hover:bg-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
              aria-label="Page précédente"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-4 py-2 text-sm text-zinc-700 dark:text-zinc-300">
              Page {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 rounded-lg bg-zinc-100 text-zinc-700 hover:bg-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
              aria-label="Page suivante"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
